/**
 * T-26: Offline Scene Management
 * 
 * Handles caching and retrieval of scenes for offline access
 * Integrates with T-22 Share Flow and T-20 Scene Serialization
 */

interface CachedScene {
    id: string;
    data: any;
    metadata: {
      timestamp: string;
      size: number;
      accessed: string;
      version: string;
      title?: string;
    };
  }
  
  interface OfflineScenesList {
    scenes: string[];
    lastUpdated: string;
    totalSize: number;
  }
  
  // Configuration
  const MAX_CACHED_SCENES = 50;
  const MAX_CACHE_SIZE = 50 * 1024 * 1024; // 50MB
  const CACHE_EXPIRY_DAYS = 30;
  
  // Storage keys
  const OFFLINE_SCENES_KEY = 'offline-scenes-list';
  const SCENE_KEY_PREFIX = 'offline-scene-';
  
  /**
   * Cache scene data for offline access
   */
  export async function cacheSceneForOffline(sceneId: string, sceneData: any, title?: string): Promise<void> {
    try {
      console.log('[Offline] Caching scene for offline access:', sceneId);
      
      // Prepare cached scene object
      const cached: CachedScene = {
        id: sceneId,
        data: sceneData,
        metadata: {
          timestamp: new Date().toISOString(),
          accessed: new Date().toISOString(),
          size: calculateSceneSize(sceneData),
          version: sceneData.schemaVersion || '1.0.0',
          title: title || `Scene ${sceneId}`
        }
      };
      
      // Store scene data
      await setStorageItem(`${SCENE_KEY_PREFIX}${sceneId}`, cached);
      
      // Update scenes list
      await updateOfflineScenesList(sceneId, cached.metadata.size);
      
      console.log('[Offline] Scene cached successfully:', sceneId);
      
    } catch (error) {
      console.warn('[Offline] Failed to cache scene:', sceneId, error);
      throw new Error(`Failed to cache scene offline: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }
  
  /**
   * Retrieve cached scene for offline use
   */
  export async function getCachedScene(sceneId: string): Promise<any | null> {
    try {
      console.log('[Offline] Retrieving cached scene:', sceneId);
      
      const cached = await getStorageItem<CachedScene>(`${SCENE_KEY_PREFIX}${sceneId}`);
      
      if (!cached) {
        console.log('[Offline] Scene not found in cache:', sceneId);
        return null;
      }
      
      // Check if scene has expired
      if (isSceneExpired(cached.metadata.timestamp)) {
        console.log('[Offline] Cached scene expired, removing:', sceneId);
        await removeCachedScene(sceneId);
        return null;
      }
      
      // Update access time
      cached.metadata.accessed = new Date().toISOString();
      await setStorageItem(`${SCENE_KEY_PREFIX}${sceneId}`, cached);
      
      console.log('[Offline] Serving scene from cache:', sceneId);
      return cached.data;
      
    } catch (error) {
      console.warn('[Offline] Failed to retrieve cached scene:', sceneId, error);
      return null;
    }
  }
  
  /**
   * Get list of offline cached scenes
   */
  export async function getOfflineScenesList(): Promise<string[]> {
    try {
      const list = await getStorageItem<OfflineScenesList>(OFFLINE_SCENES_KEY);
      return list?.scenes || [];
    } catch (error) {
      console.warn('[Offline] Failed to get offline scenes list:', error);
      return [];
    }
  }
  
  /**
   * Get detailed information about cached scenes
   */
  export async function getCachedScenesInfo(): Promise<CachedScene[]> {
    try {
      const sceneIds = await getOfflineScenesList();
      const scenes: CachedScene[] = [];
      
      for (const sceneId of sceneIds) {
        const cached = await getStorageItem<CachedScene>(`${SCENE_KEY_PREFIX}${sceneId}`);
        if (cached) {
          // Only include metadata for privacy
          scenes.push({
            id: cached.id,
            data: null, // Don't include actual data in list
            metadata: cached.metadata
          });
        }
      }
      
      return scenes.sort((a, b) => 
        new Date(b.metadata.accessed).getTime() - new Date(a.metadata.accessed).getTime()
      );
      
    } catch (error) {
      console.warn('[Offline] Failed to get cached scenes info:', error);
      return [];
    }
  }
  
  /**
   * Remove a cached scene
   */
  export async function removeCachedScene(sceneId: string): Promise<void> {
    try {
      console.log('[Offline] Removing cached scene:', sceneId);
      
      // Get scene size before removing
      const cached = await getStorageItem<CachedScene>(`${SCENE_KEY_PREFIX}${sceneId}`);
      const sceneSize = cached?.metadata.size || 0;
      
      // Remove scene data
      await removeStorageItem(`${SCENE_KEY_PREFIX}${sceneId}`);
      
      // Update scenes list
      const list = await getStorageItem<OfflineScenesList>(OFFLINE_SCENES_KEY);
      if (list) {
        list.scenes = list.scenes.filter(id => id !== sceneId);
        list.totalSize = Math.max(0, list.totalSize - sceneSize);
        list.lastUpdated = new Date().toISOString();
        await setStorageItem(OFFLINE_SCENES_KEY, list);
      }
      
      console.log('[Offline] Scene removed from cache:', sceneId);
      
    } catch (error) {
      console.warn('[Offline] Failed to remove cached scene:', sceneId, error);
      throw error;
    }
  }
  
  /**
   * Clear all cached scenes
   */
  export async function clearAllCachedScenes(): Promise<void> {
    try {
      console.log('[Offline] Clearing all cached scenes');
      
      const sceneIds = await getOfflineScenesList();
      
      // Remove all scene data
      await Promise.all(
        sceneIds.map(sceneId => removeStorageItem(`${SCENE_KEY_PREFIX}${sceneId}`))
      );
      
      // Clear scenes list
      await removeStorageItem(OFFLINE_SCENES_KEY);
      
      console.log('[Offline] All cached scenes cleared');
      
    } catch (error) {
      console.warn('[Offline] Failed to clear cached scenes:', error);
      throw error;
    }
  }
  
  /**
   * Get total cache size and statistics
   */
  export async function getCacheStatistics(): Promise<{
    totalScenes: number;
    totalSize: number;
    oldestScene: string | null;
    newestScene: string | null;
    lastAccessed: string | null;
  }> {
    try {
      const scenes = await getCachedScenesInfo();
      
      if (scenes.length === 0) {
        return {
          totalScenes: 0,
          totalSize: 0,
          oldestScene: null,
          newestScene: null,
          lastAccessed: null
        };
      }
      
      const totalSize = scenes.reduce((sum, scene) => sum + scene.metadata.size, 0);
      const sortedByTimestamp = [...scenes].sort((a, b) => 
        new Date(a.metadata.timestamp).getTime() - new Date(b.metadata.timestamp).getTime()
      );
      const sortedByAccessed = [...scenes].sort((a, b) => 
        new Date(b.metadata.accessed).getTime() - new Date(a.metadata.accessed).getTime()
      );
      
      return {
        totalScenes: scenes.length,
        totalSize,
        oldestScene: sortedByTimestamp[0]?.id || null,
        newestScene: sortedByTimestamp[sortedByTimestamp.length - 1]?.id || null,
        lastAccessed: sortedByAccessed[0]?.metadata.accessed || null
      };
      
    } catch (error) {
      console.warn('[Offline] Failed to get cache statistics:', error);
      return {
        totalScenes: 0,
        totalSize: 0,
        oldestScene: null,
        newestScene: null,
        lastAccessed: null
      };
    }
  }
  
  /**
   * Cleanup expired and excess cached scenes
   */
  export async function cleanupCachedScenes(): Promise<void> {
    try {
      console.log('[Offline] Starting cache cleanup');
      
      const scenes = await getCachedScenesInfo();
      const now = new Date();
      
      // Remove expired scenes
      for (const scene of scenes) {
        if (isSceneExpired(scene.metadata.timestamp)) {
          await removeCachedScene(scene.id);
        }
      }
      
      // Get updated list after expiry cleanup
      const remainingScenes = await getCachedScenesInfo();
      
      // Remove excess scenes (keeping most recently accessed)
      if (remainingScenes.length > MAX_CACHED_SCENES) {
        const sortedByAccess = remainingScenes.sort((a, b) => 
          new Date(a.metadata.accessed).getTime() - new Date(b.metadata.accessed).getTime()
        );
        
        const scenesToRemove = sortedByAccess.slice(0, remainingScenes.length - MAX_CACHED_SCENES);
        for (const scene of scenesToRemove) {
          await removeCachedScene(scene.id);
        }
      }
      
      // Check total size and remove oldest if needed
      const stats = await getCacheStatistics();
      if (stats.totalSize > MAX_CACHE_SIZE) {
        const sortedByTimestamp = remainingScenes.sort((a, b) => 
          new Date(a.metadata.timestamp).getTime() - new Date(b.metadata.timestamp).getTime()
        );
        
        let currentSize = stats.totalSize;
        for (const scene of sortedByTimestamp) {
          if (currentSize <= MAX_CACHE_SIZE) break;
          await removeCachedScene(scene.id);
          currentSize -= scene.metadata.size;
        }
      }
      
      console.log('[Offline] Cache cleanup completed');
      
    } catch (error) {
      console.warn('[Offline] Cache cleanup failed:', error);
    }
  }
  
  // ============================================================================
  // HELPER FUNCTIONS
  // ============================================================================
  
  /**
   * Update the offline scenes list
   */
  async function updateOfflineScenesList(sceneId: string, sceneSize: number): Promise<void> {
    try {
      let list = await getStorageItem<OfflineScenesList>(OFFLINE_SCENES_KEY);
      
      if (!list) {
        list = {
          scenes: [],
          lastUpdated: new Date().toISOString(),
          totalSize: 0
        };
      }
      
      // Add scene to list if not already present
      if (!list.scenes.includes(sceneId)) {
        list.scenes.unshift(sceneId);
        list.totalSize += sceneSize;
      }
      
      // Limit list size
      if (list.scenes.length > MAX_CACHED_SCENES) {
        const removedScenes = list.scenes.splice(MAX_CACHED_SCENES);
        // Note: We don't remove the actual scene data here as it will be cleaned up later
      }
      
      list.lastUpdated = new Date().toISOString();
      await setStorageItem(OFFLINE_SCENES_KEY, list);
      
    } catch (error) {
      console.warn('[Offline] Failed to update scenes list:', error);
      throw error;
    }
  }
  
  /**
   * Calculate scene data size
   */
  function calculateSceneSize(sceneData: any): number {
    try {
      return JSON.stringify(sceneData).length;
    } catch (error) {
      console.warn('[Offline] Failed to calculate scene size:', error);
      return 0;
    }
  }
  
  /**
   * Check if a scene has expired
   */
  function isSceneExpired(timestamp: string): boolean {
    const now = new Date();
    const sceneDate = new Date(timestamp);
    const diffDays = (now.getTime() - sceneDate.getTime()) / (1000 * 60 * 60 * 24);
    return diffDays > CACHE_EXPIRY_DAYS;
  }
  
  /**
   * Storage abstraction - uses localStorage with JSON serialization
   */
  async function setStorageItem(key: string, value: any): Promise<void> {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch (error) {
      // Handle quota exceeded error
      if (error instanceof DOMException && error.code === 22) {
        console.warn('[Offline] Storage quota exceeded, cleaning up...');
        await cleanupCachedScenes();
        // Try again after cleanup
        localStorage.setItem(key, JSON.stringify(value));
      } else {
        throw error;
      }
    }
  }
  
  /**
   * Get item from storage
   */
  async function getStorageItem<T>(key: string): Promise<T | null> {
    try {
      const item = localStorage.getItem(key);
      return item ? JSON.parse(item) : null;
    } catch (error) {
      console.warn('[Offline] Failed to parse stored item:', key, error);
      return null;
    }
  }
  
  /**
   * Remove item from storage
   */
  async function removeStorageItem(key: string): Promise<void> {
    localStorage.removeItem(key);
  }
  
  /**
   * Format cache size for display (exported utility)
   */
  export function formatCacheSize(bytes: number): string {
    if (bytes === 0) return '0 B';
    
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  }